import { useState, useRef, useEffect } from 'preact/hooks';
import './app.css';
import { MessageIcon } from './components/message-icon';
import { NotificationDropdown } from './components/notification-dropdown';
import { useNotifications } from './hooks/useNotifications';
import { useNotificationCount } from './hooks/useNotificationCount';

export function App({ userId }: { userId: number }) {
  const [active, setActive] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasAttemptedMarkSeenThisOpen, setHasAttemptedMarkSeenThisOpen] =
    useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    markAsSeen,
    markAllAsSeen,
    markAsRead,
    markAllAsRead,
    notificationsQuerySuccess,
  } = useNotifications(isDropdownOpen, userId);

  // Use the Socket.IO hook for real-time notification count
  const {
    count: wsCount,
    isConnected,
    error: wsError,
  } = useNotificationCount();

  // Use WebSocket count as the primary source
  const count = wsCount;
  console.log('Current notification state:', {
    wsCount,
    isConnected,
    wsError: wsError?.message,
    finalCount: count,
  });

  // Log connection status changes
  useEffect(() => {
    console.log('WebSocket connection status changed:', {
      isConnected,
      error: wsError?.message,
    });
  }, [isConnected, wsError]);

  // Set active state based on count
  useEffect(() => {
    setActive(count > 0);
  }, [count]);

  const handleIconClick = () => {
    const newDropdownState = !isDropdownOpen;
    setIsDropdownOpen(newDropdownState);
    setActive(newDropdownState); // Keep icon active while dropdown is open
    // markAllAsSeen will now be handled by a useEffect after notifications are loaded
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
    setActive(false);
    setHasAttemptedMarkSeenThisOpen(false); // Reset flag when dropdown closes
  };

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isDropdownOpen
      ) {
        handleCloseDropdown();
      }
    };

    // Add event listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleMarkAsSeen = (id: string) => {
    markAsSeen(id);
    // Optimistic update in hook, WebSocket might also update count
  };

  const handleMarkAsRead = (id: string) => {
    markAsRead(id);
    // Optimistic update in hook, WebSocket might also update count
  };

  const handleMarkAllAsSeen = () => {
    markAllAsSeen();
    // Count should update via WebSocket or optimistic update in hook
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    // Count should update via WebSocket or optimistic update in hook
    // Potentially close dropdown or give feedback
  };

  // Effect to mark all as seen after notifications are loaded and dropdown is open
  useEffect(() => {
    if (
      isDropdownOpen &&
      notificationsQuerySuccess &&
      !hasAttemptedMarkSeenThisOpen
    ) {
      markAllAsSeen();
      setHasAttemptedMarkSeenThisOpen(true);
    }
  }, [
    isDropdownOpen,
    notificationsQuerySuccess,
    hasAttemptedMarkSeenThisOpen,
    markAllAsSeen,
  ]);

  return (
    <div class="notifier-app">
      <div className="notifier-container" ref={containerRef}>
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '5px' }}>
            WS: {isConnected ? '✅' : '❌'} Count: {count}{' '}
            {wsError && `Error: ${wsError.message}`}
          </div>
        )}

        <button
          onClick={handleIconClick}
          className="clear"
          style={{ cursor: 'pointer' }}
        >
          <MessageIcon active={active} count={count} />
        </button>

        <NotificationDropdown
          isOpen={isDropdownOpen}
          notifications={notifications}
          onClose={handleCloseDropdown}
          onMarkAsSeen={handleMarkAsSeen} // Individual item: (id: string) => void
          onMarkAllAsSeen={handleMarkAllAsSeen} // Button: () => void
          onMarkAsRead={handleMarkAsRead} // Individual item: (id: string) => void
          onMarkAllAsRead={handleMarkAllAsRead} // Button: () => void
        />
      </div>
    </div>
  );
}
